import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getTickets, getClientDashboardStats } from '../lib/api';
import { statusConfig, priorityConfig, formatRelativeDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { 
    Ticket, 
    Plus, 
    Search, 
    LogOut, 
    Clock, 
    CheckCircle, 
    AlertCircle,
    ChevronRight,
    Filter,
    Settings
} from 'lucide-react';

export default function ClientDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [tickets, setTickets] = useState([]);
    const [stats, setStats] = useState({ totale: 0, aperti: 0, risolti: 0 });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [ticketsRes, statsRes] = await Promise.all([
                getTickets(),
                getClientDashboardStats()
            ]);
            setTickets(ticketsRes.data);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredTickets = tickets.filter(ticket => {
        const matchesSearch = ticket.oggetto.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              ticket.numero_ticket.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || ticket.stato === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="glass-header sticky top-0 z-50 border-b border-slate-200 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center">
                                <Ticket className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-lg font-bold text-slate-900">Portale Assistenza</h1>
                                <p className="text-xs text-slate-500">Ciao, {user?.nome}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button 
                                variant="ghost" 
                                onClick={() => navigate('/settings')}
                                data-testid="settings-btn"
                            >
                                <Settings className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn">
                                <LogOut className="w-4 h-4 mr-2" />
                                Esci
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <Card className="border-slate-200">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-medium text-slate-600">Totale Ticket</CardTitle>
                            <Ticket className="w-4 h-4 text-slate-400" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-3xl font-bold text-slate-900">{stats.totale}</div>
                        </CardContent>
                    </Card>
                    <Card className="border-slate-200">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-medium text-slate-600">In Corso</CardTitle>
                            <Clock className="w-4 h-4 text-amber-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-3xl font-bold text-amber-600">{stats.aperti}</div>
                        </CardContent>
                    </Card>
                    <Card className="border-slate-200">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-medium text-slate-600">Risolti</CardTitle>
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-3xl font-bold text-emerald-600">{stats.risolti}</div>
                        </CardContent>
                    </Card>
                </div>

                {/* Actions & Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                    <Link to="/tickets/new" className="sm:order-last">
                        <Button className="w-full sm:w-auto bg-indigo-500 hover:bg-indigo-600" data-testid="create-ticket-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Nuovo Ticket
                        </Button>
                    </Link>
                    <div className="flex-1 flex gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                placeholder="Cerca ticket..."
                                className="pl-10"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                data-testid="search-tickets-input"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-[180px]" data-testid="status-filter-select">
                                <Filter className="w-4 h-4 mr-2" />
                                <SelectValue placeholder="Stato" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutti gli stati</SelectItem>
                                <SelectItem value="Aperto">Aperto</SelectItem>
                                <SelectItem value="In attesa risposta">In attesa risposta</SelectItem>
                                <SelectItem value="In lavorazione">In lavorazione</SelectItem>
                                <SelectItem value="Risolto">Risolto</SelectItem>
                                <SelectItem value="Chiuso">Chiuso</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Tickets List */}
                <div className="space-y-4">
                    {loading ? (
                        <div className="text-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto"></div>
                            <p className="mt-4 text-slate-500">Caricamento...</p>
                        </div>
                    ) : filteredTickets.length === 0 ? (
                        <Card className="border-slate-200">
                            <CardContent className="flex flex-col items-center justify-center py-12">
                                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                                    <AlertCircle className="w-8 h-8 text-slate-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-900 mb-2">Nessun ticket trovato</h3>
                                <p className="text-slate-500 mb-4">Non hai ancora aperto ticket di assistenza</p>
                                <Link to="/tickets/new">
                                    <Button className="bg-indigo-500 hover:bg-indigo-600">
                                        <Plus className="w-4 h-4 mr-2" />
                                        Crea il tuo primo ticket
                                    </Button>
                                </Link>
                            </CardContent>
                        </Card>
                    ) : (
                        filteredTickets.map((ticket, index) => (
                            <Link 
                                key={ticket.id} 
                                to={`/tickets/${ticket.id}`}
                                className="block animate-slide-in"
                                style={{ animationDelay: `${index * 50}ms` }}
                                data-testid={`ticket-card-${ticket.id}`}
                            >
                                <Card className="ticket-card border-slate-200 hover:border-indigo-200 cursor-pointer">
                                    <CardContent className="p-4 sm:p-6">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <code className="text-xs font-mono text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                                                        {ticket.numero_ticket}
                                                    </code>
                                                    <Badge 
                                                        variant="outline" 
                                                        className={`${statusConfig[ticket.stato]?.bg} ${statusConfig[ticket.stato]?.text} ${statusConfig[ticket.stato]?.border}`}
                                                    >
                                                        {ticket.stato}
                                                    </Badge>
                                                    <Badge 
                                                        variant="outline" 
                                                        className={`${priorityConfig[ticket.priorita]?.bg} ${priorityConfig[ticket.priorita]?.text}`}
                                                    >
                                                        {ticket.priorita}
                                                    </Badge>
                                                </div>
                                                <h3 className="font-semibold text-slate-900 truncate">{ticket.oggetto}</h3>
                                                <p className="text-sm text-slate-500 mt-1 line-clamp-1">{ticket.descrizione}</p>
                                                <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                                                    <span>{ticket.categoria}</span>
                                                    <span>•</span>
                                                    <span>{formatRelativeDate(ticket.created_at)}</span>
                                                    {ticket.commenti?.length > 0 && (
                                                        <>
                                                            <span>•</span>
                                                            <span>{ticket.commenti.length} risposte</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
                                        </div>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))
                    )}
                </div>
            </main>
        </div>
    );
}
